import { Gesture } from '@use-gesture/vanilla';
import 'Components/vm-modal/vm-modal';
import { html, LitElement, type PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import Styles from './vm-image-lightbox.scss';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SWIPE_DISMISS_DISTANCE_RATIO = 0.3;
const SWIPE_DISMISS_VELOCITY = 0.5;
const TRANSFORM_TRANSITION_MS = 200;

export interface LightboxShowDetail {
    src: string;
    alt: string;
}

/**
 * An image viewer built on top of `vm-modal` in its `lightbox` variant. Full-bleed on
 * mobile (opaque backdrop), centered at natural size on desktop (capped at 90vw/90vh,
 * semi-transparent backdrop). Supports pinch-to-zoom (1×–4×) and drag-to-pan when
 * zoomed. At 1×, dragging down past ~30% of the viewport (or with sufficient velocity)
 * dismisses with an off-screen animation; shorter drags snap back. The transform is
 * reset on close.
 *
 * **Consumer pattern**: mount a single `<vm-image-lightbox>` in the page template. Any
 * element on the page dispatches `vm-image-lightbox:open` with `{ src, alt }` (bubbles +
 * composed) — the lightbox listens on `document`, self-opens with that image, and
 * self-closes on dismiss. No glue script required. The `open` / `src` / `alt` props
 * remain controllable for callers that want full programmatic control instead.
 *
 * **Multiple instances**: only the first-mounted lightbox claims the `document`
 * listener. Any later-mounted lightbox logs a console warning and won't respond to
 * dispatch-driven open — keep one per page, or drive extras programmatically.
 *
 * **Opting out of a specific event**: register a listener on the dispatcher (or any
 * ancestor) that calls `e.preventDefault()`. Because the event bubbles + composes,
 * ancestors see it first and can intercept. For events dispatched directly on
 * `document`, registration order matters: use `addEventListener(..., { capture: true })`
 * so your interceptor runs before the lightbox's listener.
 *
 * @fires vm-close - Fired when the lightbox is dismissed via the close button, swipe-down,
 *                   Escape, backdrop click, or programmatic `open=false`. Bubbles up from the
 *                   inner `vm-modal` and is retargeted to this host by the shadow boundary.
 *
 * @example
 * ```html
 * <!-- Page template: mount once. -->
 * <vm-image-lightbox></vm-image-lightbox>
 *
 * <!-- Open from any element on the page — event bubbles up to document. -->
 * <button id="open-photo">Open photo</button>
 * <script>
 *   document.getElementById('open-photo').addEventListener('click', (e) => {
 *     e.currentTarget.dispatchEvent(new CustomEvent('vm-image-lightbox:open', {
 *       detail: { src: '/photo.jpg', alt: 'Sample' },
 *       bubbles: true, composed: true, cancelable: true,
 *     }));
 *   });
 * </script>
 * ```
 */
@customElement('vm-image-lightbox')
export class VmImageLightbox extends LitElement {
    static override styles = Styles;

    /** Whether the lightbox is visible. */
    @property({ type: Boolean, reflect: true })
    open = false;

    /** Image source URL. */
    @property()
    src = '';

    /** Image alt text. */
    @property()
    alt = '';

    @state()
    private _scale = MIN_SCALE;

    @state()
    private _x = 0;

    @state()
    private _y = 0;

    @state()
    private _animating = false;

    @query('.vm-image-lightbox__surface')
    private _surface!: HTMLDivElement;

    @query('img')
    private _image!: HTMLImageElement;

    private _gesture: Gesture | null = null;
    private _pendingDismiss = false;

    /**
     * First-mounted-wins claim on the `document`-level `vm-image-lightbox:open` listener.
     * Prevents multiple instances from double-opening on one dispatch.
     */
    private static _activeListener: VmImageLightbox | null = null;

    override updated(changed: PropertyValues<this>): void {
        if (!changed.has('open')) return;
        if (this.open) {
            // Wait for render so `_surface` exists. Re-check `open` inside the microtask —
            // a rapid true→false→true sequence could otherwise create a stale gesture on
            // a closed lightbox.
            this.updateComplete.then(() => {
                if (this.open) this._attachGesture();
            });
        } else {
            this._detachGesture();
            this._resetTransform();
        }
    }

    override connectedCallback(): void {
        super.connectedCallback();
        if (VmImageLightbox._activeListener && VmImageLightbox._activeListener !== this) {
            // eslint-disable-next-line no-console
            console.warn(
                '<vm-image-lightbox>: another instance already listens for `vm-image-lightbox:open`; this one will not respond to dispatch-driven open. Mount one lightbox per page, or drive extras via the `open` / `src` / `alt` props.',
            );
            return;
        }
        VmImageLightbox._activeListener = this;
        document.addEventListener('vm-image-lightbox:open', this._onShowEvent);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (VmImageLightbox._activeListener === this) {
            document.removeEventListener('vm-image-lightbox:open', this._onShowEvent);
            VmImageLightbox._activeListener = null;
        }
        this._detachGesture();
    }

    private _onShowEvent = (e: Event): void => {
        if (e.defaultPrevented) return;
        const detail = (e as CustomEvent<LightboxShowDetail>).detail;
        if (!detail || typeof detail.src !== 'string' || typeof detail.alt !== 'string') return;
        // Reset transform state so a new image opened while one is already showing
        // doesn't inherit the previous image's zoom/pan.
        this._resetTransform();
        this.src = detail.src;
        this.alt = detail.alt;
        this.open = true;
    };

    private _resetTransform(): void {
        this._scale = MIN_SCALE;
        this._x = 0;
        this._y = 0;
        this._animating = false;
        this._pendingDismiss = false;
    }

    private _attachGesture(): void {
        if (!this._surface || this._gesture) return;
        this._gesture = new Gesture(
            this._surface,
            {
                onPinch: ({ offset: [scale], last }) => {
                    this._scale = scale;
                    this._animating = false;
                    this._pendingDismiss = false;
                    // After a pinch back to 1×, snap the image to center so a subsequent
                    // swipe-down starts from the expected position.
                    if (last && scale <= MIN_SCALE) {
                        this._animating = true;
                        this._x = 0;
                        this._y = 0;
                    }
                },
                onDrag: ({ offset: [ox, oy], movement: [, my], velocity: [, vy], pinching, last, cancel }) => {
                    if (pinching) return cancel();

                    if (this._scale > MIN_SCALE) {
                        // Pan-when-zoomed.
                        this._animating = false;
                        this._pendingDismiss = false;
                        this._x = ox;
                        this._y = oy;
                        return;
                    }

                    // Swipe-down at 1×. Only track downward movement (no upward drag).
                    if (last) {
                        const threshold = window.innerHeight * SWIPE_DISMISS_DISTANCE_RATIO;
                        const shouldDismiss = my > threshold || vy > SWIPE_DISMISS_VELOCITY;
                        this._animating = true;
                        if (shouldDismiss) {
                            // Continue off-screen, then close in `_onTransitionEnd`.
                            this._pendingDismiss = true;
                            this._y = window.innerHeight;
                        } else {
                            this._pendingDismiss = false;
                            this._y = 0;
                        }
                        return;
                    }

                    this._animating = false;
                    this._pendingDismiss = false;
                    this._x = 0;
                    this._y = Math.max(0, my);
                },
            },
            {
                pinch: { scaleBounds: { min: MIN_SCALE, max: MAX_SCALE }, rubberband: true },
                drag: {
                    from: () => [this._x, this._y],
                    // Clamp the pan offset so a zoomed image can't be dragged fully off-screen.
                    // Only active when zoomed — at 1× we leave drag unbounded so swipe-down
                    // dismiss (which lets `movement.y` grow past the viewport) still works.
                    bounds: () => (this._scale > MIN_SCALE ? this._panBounds() : {}),
                },
            },
        );
    }

    private _detachGesture(): void {
        this._gesture?.destroy();
        this._gesture = null;
    }

    /**
     * Max pan offset for the current zoom — derived from the overflow between the image's
     * rendered size (after `scale`) and the surface that contains it. `offsetWidth/Height`
     * give the pre-transform layout size, which is the right basis to multiply by `scale`.
     * Only called when zoomed (`scale > 1`); the 1× case is short-circuited at the call site.
     */
    private _panBounds(): { left: number; right: number; top: number; bottom: number } {
        const img = this._image;
        const surface = this._surface;
        if (!img || !surface) return { left: 0, right: 0, top: 0, bottom: 0 };
        const overflowX = Math.max(0, (img.offsetWidth * this._scale - surface.offsetWidth) / 2);
        const overflowY = Math.max(0, (img.offsetHeight * this._scale - surface.offsetHeight) / 2);
        return { left: -overflowX, right: overflowX, top: -overflowY, bottom: overflowY };
    }

    private _onModalClose(): void {
        // Keep our `open` in sync when the modal closes itself (Escape, backdrop, or its
        // built-in close button). The modal's `vm-close` bubbles past this listener and
        // is retargeted to this host, so consumers see a single event.
        this.open = false;
    }

    private _onTransitionEnd(e: TransitionEvent): void {
        if (e.propertyName !== 'transform') return;
        this._animating = false;
        if (this._pendingDismiss) {
            this._pendingDismiss = false;
            this.open = false;
        }
    }

    override render() {
        const transform = `translate3d(${this._x}px, ${this._y}px, 0) scale(${this._scale})`;
        const transition = this._animating ? `transform ${TRANSFORM_TRANSITION_MS}ms ease-out` : 'none';
        return html`
            <vm-modal variant="lightbox" ?open=${this.open} @vm-close=${this._onModalClose}>
                <span slot="header">Image viewer</span>
                <div class="vm-image-lightbox__surface">
                    <img
                        class="vm-image-lightbox__image"
                        src=${this.src}
                        alt=${this.alt}
                        style="transform: ${transform}; transition: ${transition};"
                        draggable="false"
                        @transitionend=${this._onTransitionEnd}
                    />
                </div>
            </vm-modal>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-image-lightbox': VmImageLightbox;
    }
    interface HTMLElementEventMap {
        'vm-close': CustomEvent;
        'vm-image-lightbox:open': CustomEvent<LightboxShowDetail>;
    }
    interface DocumentEventMap {
        'vm-image-lightbox:open': CustomEvent<LightboxShowDetail>;
    }
}
